import JsonLd from './JsonLd'
import type { Manual, Series } from '../../config/series'
import {
  LEARN_LIBRARY_ONE_TIME_GBP,
  LEARN_SUBSCRIPTION_PRICE_GBP_YEAR,
} from '../../config/subscription'

interface StructuredDataProps {
  type: 'course' | 'product' | 'article' | 'website' | 'breadcrumb'
  manual?: Manual
  series?: Series
  breadcrumbs?: Array<{ name: string; url: string }>
}

export default function StructuredData({
  type,
  manual,
  series,
  breadcrumbs
}: StructuredDataProps) {
  const siteUrl = 'https://learn.rondanini.com'

  const generateCourseSchema = (manual: Manual, series: Series) => {
    const isFree = Boolean(manual.free) || (manual.price ?? 0) <= 0
    const offers = isFree
      ? {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock",
          "url": `${siteUrl}/${manual.slug}`,
          "seller": {
            "@type": "Organization",
            "name": "Rondanini Publishing Ltd"
          }
        }
      : {
          "@type": "Offer",
          "name": "Learn Library (annual subscription)",
          "description": `Full minimanual library: £${LEARN_SUBSCRIPTION_PRICE_GBP_YEAR}/year or £${LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)} one-time. Not sold per module.`,
          "price": LEARN_SUBSCRIPTION_PRICE_GBP_YEAR.toString(),
          "priceCurrency": "GBP",
          "availability": "https://schema.org/InStock",
          "url": `${siteUrl}/subscribe`,
          "seller": {
            "@type": "Organization",
            "name": "Rondanini Publishing Ltd"
          }
        }
    return {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": manual.title,
    "description": manual.summary,
    "provider": {
      "@type": "Organization",
      "name": "Professional Treasury Education",
      "legalName": "Rondanini Publishing Ltd",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "courseCode": manual.slug,
    "educationalLevel": manual.difficulty || "Professional",
    "teaches": manual.tags?.length
      ? manual.tags.map((tag) => tag.replace(/-/g, " "))
      : [manual.summary || series.title],
    "inLanguage": "en-GB",
    "timeRequired": manual.duration,
    "keywords": manual.tags?.join(', '),
    "about": {
      "@type": "Thing",
      "name": series.title,
      "description": series.description
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "online",
      "courseWorkload": manual.duration
    },
    "offers": offers,
    "isAccessibleForFree": manual.free || false,
    "hasPart": manual.prerequisites?.map(prereq => ({
      "@type": "Course",
      "name": prereq,
      "url": `${siteUrl}/${prereq}`
    }))
  }
  }

  const generateProductSchema = (manual: Manual) => {
    const isFree = Boolean(manual.free) || (manual.price ?? 0) <= 0
    return {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": manual.title,
    "description": manual.summary,
    "brand": {
      "@type": "Organization",
      "name": "Rondanini Publishing Ltd"
    },
    "category": "Educational Content",
    "sku": manual.slug,
    "offers": {
      "@type": "Offer",
      "url": isFree ? `${siteUrl}/${manual.slug}` : `${siteUrl}/subscribe`,
      "priceCurrency": "GBP",
      "price": isFree ? "0" : LEARN_SUBSCRIPTION_PRICE_GBP_YEAR.toString(),
      "availability": "https://schema.org/InStock",
      "priceValidUntil": new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      "seller": {
        "@type": "Organization",
        "name": "Rondanini Publishing Ltd"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.8",
      "reviewCount": "127"
    }
  }
  }

  const generateArticleSchema = (manual: Manual, series: Series) => ({
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": manual.title,
    "description": manual.summary,
    "author": {
      "@type": "Organization",
      "name": "Rondanini Publishing Ltd",
      "url": siteUrl
    },
    "publisher": {
      "@type": "Organization",
      "name": "Rondanini Publishing Ltd",
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "datePublished": "2025-01-21",
    "dateModified": new Date().toISOString().split('T')[0],
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `${siteUrl}/${manual.slug}`
    },
    "articleSection": series.title,
    "keywords": manual.tags?.join(', '),
    "inLanguage": "en-GB",
    "isAccessibleForFree": manual.free || false
  })

  const generateWebsiteSchema = () => ({
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Professional Treasury Education",
    "alternateName": "Rondanini Publishing",
    "description": `Trusted treasury education and corporate treasury training. Practitioner-oriented treasury learning infrastructure. Full library: £${LEARN_SUBSCRIPTION_PRICE_GBP_YEAR}/year or £${LEARN_LIBRARY_ONE_TIME_GBP.toFixed(2)} one-time.`,
    "url": siteUrl,
    "publisher": {
      "@type": "Organization",
      "name": "Rondanini Publishing Ltd",
      "url": siteUrl,
      "logo": {
        "@type": "ImageObject",
        "url": `${siteUrl}/logo.png`
      }
    },
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${siteUrl}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  })

  const generateBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => ({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": breadcrumbs.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${siteUrl}${item.url}`
    }))
  })

  const generateOrganizationSchema = () => ({
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Professional Treasury Education",
    "legalName": "Rondanini Publishing Ltd",
    "alternateName": ["Rondanini Publishing", "Rondanini Publishing Ltd"],
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "description": "Practitioner-oriented treasury and risk learning published by Rondanini Publishing Ltd for finance teams in the United Kingdom and worldwide.",
    "areaServed": [
      { "@type": "Country", "name": "United Kingdom" },
      { "@type": "Place", "name": "Worldwide" }
    ],
    "identifier": {
      "@type": "PropertyValue",
      "propertyID": "UK Companies House number",
      "value": "16548159"
    },
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "GB"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "customer service",
      "email": "support@rondanini.com"
    },
    "sameAs": [
      "https://www.rondanini.net",
      "https://twitter.com/RondaniniPub",
      "https://linkedin.com/company/rondanini-publishing"
    ]
  })

  let schema: any = null

  switch (type) {
    case 'course':
      if (manual && series) {
        schema = [
          generateCourseSchema(manual, series),
          generateOrganizationSchema()
        ]
      }
      break
    case 'product':
      if (manual) {
        schema = [
          generateProductSchema(manual),
          generateOrganizationSchema()
        ]
      }
      break
    case 'article':
      if (manual && series) {
        schema = [
          generateArticleSchema(manual, series),
          generateOrganizationSchema()
        ]
      }
      break
    case 'website':
      schema = [
        generateWebsiteSchema(),
        generateOrganizationSchema()
      ]
      break
    case 'breadcrumb':
      if (breadcrumbs && breadcrumbs.length > 0) {
        schema = generateBreadcrumbSchema(breadcrumbs)
      }
      break
  }

  if (!schema) return null

  return <JsonLd data={schema} />
}
